/* eslint-disable max-lines */
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { ValidationError } from 'yup';
import LanguageSwitcher from '@components/shared/language-switcher/language-switcher.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { css, StyleSheet } from 'aphrodite';
import cn from 'classnames';
import Input from '@components/atoms/ui-kit/input.tsx';
import { useActivateUser } from '@utils/api/organizations/use-activate-organization.tsx';
import { PATHS } from '../../router/paths.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import PublicLogo from '@components/shared/public-logo.tsx';

interface ActivateUserFormValues {
  email: string;
  activation_key: string;
  password: string;
  password_confirmation: string;
}

const PasswordCriteria = ({ password }: { password: string }) => {
  const { t } = useTranslation();

  const criteria = useMemo(
    () => [
      {
        label: t('password-must-be-at-least-12-characters-long'),
        valid: password.length >= 12
      },
      {
        label: t('password-must-have-at-least-one-uppercase-letter'),
        valid: /[A-Z]/.test(password)
      },
      {
        label: t('password-must-have-at-least-one-lowercase-letter'),
        valid: /[a-z]/.test(password)
      },
      {
        label: t('password-must-have-at-least-one-number'),
        valid: /\d/.test(password)
      }
    ],
    [password, t]
  );

  return (
    <ul className="text-sm mt-2 space-y-1.5">
      {criteria.map((item, idx) => (
        <li
          key={idx}
          className={
            item.valid
              ? 'text-green-600 dark:text-green-400'
              : 'text-gray-500 dark:text-zinc-400'
          }
        >
          {item.valid ? '✓' : '○'} {item.label}
        </li>
      ))}
    </ul>
  );
};

const ActivateUserForm = () => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const navigate = useNavigate();
  const { mutate, isLoading } = useActivateUser();
  const [searchParams] = useSearchParams();

  const rawEmail = searchParams.get('email');
  const emailFromUrl = rawEmail ? rawEmail.replace(/ /g, '+') : '';
  const activationKeyFromUrl = searchParams.get('activation_key') || '';

  const styles = useMemo(
    () =>
      StyleSheet.create({
        pageText: { color: getColour('normalText') },
        cardBg: { backgroundColor: getColour('secondLayer') }
      }),
    [getColour]
  );

  const validationSchema = Yup.object({
    email: Yup.string()
      .required(t('email-required'))
      .email(t('email-is-invalid')),
    activation_key: Yup.string().required(t('activation-key-required')),
    password: Yup.string()
      .required(t('new-password-is-required'))
      .min(12, t('password-must-be-at-least-12-characters'))
      .matches(/[A-Z]/, t('password-must-have-at-least-one-uppercase-letter'))
      .matches(/[a-z]/, t('password-must-have-at-least-one-lowercase-letter'))
      .matches(/\d/, t('password-must-have-at-least-one-number')),
    password_confirmation: Yup.string()
      .required(t('confirm-password-is-required'))
      .oneOf([Yup.ref('password')], t('passwords-must-match'))
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<ActivateUserFormValues>({
    defaultValues: {
      email: emailFromUrl,
      activation_key: activationKeyFromUrl,
      password: '',
      password_confirmation: ''
    },
    resolver: yupResolver(validationSchema),
    mode: 'onBlur'
  });

  useEffect(() => {
    setValue('email', emailFromUrl);
    setValue('activation_key', activationKeyFromUrl);
  }, [emailFromUrl, activationKeyFromUrl, setValue]);

  const passwordValue = watch('password');

  const onSubmit = (values: ActivateUserFormValues) => {
    if (!values.activation_key || !values.email) {
      toast.error(t('url-is-invalid'));
      return;
    }

    mutate(
      {
        email: values.email.toLowerCase(),
        activation_code: values.activation_key,
        password: values.password
      },
      {
        onSuccess: () => {
          toast.success(t('activation-successful'));
          setTimeout(() => navigate(PATHS().LOGIN), 1500);
        },
        onError: (error: unknown) => {
          const fallback = t('activation-failed');

          // Handle ValidationError (from 422 responses)
          if (error instanceof ValidationError) {
            toast.error(error.message || fallback);
            return;
          }

          // Handle AxiosError
          if (error instanceof Error && 'isAxiosError' in error) {
            const axiosError = error as AxiosError<BaseError>;
            const data = axiosError?.response?.data;
            const message =
              data?.error_description ||
              data?.detail ||
              data?.error ||
              data?.message ||
              fallback;
            toast.error(message);
            return;
          }

          // Fallback for any other error
          toast.error(fallback);
        }
      }
    );
  };

  // inline error helper (for visible fields only)
  const InlineError = ({ msg, id }: { msg?: string; id: string }) =>
    msg ? (
      <p id={id} className="mt-1 text-xs font-medium text-red-600">
        {msg}
      </p>
    ) : null;

  return (
    <div
      className={cn(
        'min-h-screen w-full flex items-center justify-center px-4 py-10',
        css(styles.pageText)
      )}
    >
      <div
        className={cn(
          'w-full max-w-[520px] rounded-xl border border-gray-200/50 dark:border-zinc-700/50 shadow-sm p-6 md:p-8',
          css(styles.cardBg)
        )}
      >
        {/* Header */}
        <div className="flex flex-col items-center gap-3 mb-2">
          <PublicLogo className="h-auto w-[11rem]" />
          <h1 className="text-2xl font-bold leading-8 tracking-tight">
            {t('account-activation')}
          </h1>
          <p className="text-sm text-center">
            {t('already-have-account')}{' '}
            <button
              type="button"
              onClick={() => navigate(PATHS().LOGIN)}
              className="font-semibold text-primary hover:underline"
            >
              {t('login-here')}
            </button>
          </p>
          <div className="flex justify-center">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Subtext */}
        <p className="mt-2 mb-4 text-sm text-center">
          {t('create-a-password-at-least-12-characters-long')}
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Hidden fields: preserve exact behavior while keeping UI clean */}
          <input type="hidden" {...register('email')} />
          <input type="hidden" {...register('activation_key')} />

          {/* Password */}
          <div className="mb-3">
            <Input
              id="password"
              label={t('password')}
              type="password"
              autoComplete="new-password"
              className="block w-full"
              {...register('password')}
              aria-invalid={!!errors.password}
              aria-describedby="password-error"
            />
            <InlineError id="password-error" msg={errors.password?.message} />
            {passwordValue?.length > 0 && (
              <PasswordCriteria password={passwordValue} />
            )}
          </div>

          {/* Confirm Password */}
          <div className="mb-6">
            <Input
              id="password_confirmation"
              label={t('password-confirmation')}
              type="password"
              autoComplete="new-password"
              className="block w-full"
              {...register('password_confirmation')}
              aria-invalid={!!errors.password_confirmation}
              aria-describedby="password-confirmation-error"
            />
            <InlineError
              id="password-confirmation-error"
              msg={errors.password_confirmation?.message}
            />
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              label={t('cancel')}
              size="medium"
              variant="secondary"
              type="button"
              onClick={() => navigate(PATHS().LOGIN)}
            />
            <Button
              label={isSubmitting || isLoading ? t('submitting') : t('submit')}
              size="medium"
              variant="primary"
              type="submit"
              disabled={isSubmitting || isLoading}
            />
          </div>

          {/* Support line */}
          <p className="mt-4 text-xs text-center">
            {t('need-help-activating-your-account-contact-support')}:&nbsp;
            <a
              href="mailto:info@scriptix.io"
              className="text-primary font-semibold hover:underline"
            >
              Scriptix Support
            </a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default ActivateUserForm;
